import React, { useEffect, useMemo, useState } from "react";

import {
  Award,
  MoveLeft,
  MoveRight,
  Plus,
  SquarePen,
  Unlink2,
  Users,
  UsersRound,
} from "lucide-react";

import { AccountTeamCandidate } from "./accountTeamHelpers";
import AddAccountTeamModal from "./AddAccountTeamModal";
import Button from "../../../../Components/Button";
import CommonInput from "../../../../Components/Input";
import CommonModal from "../../../../Components/Modal";
import Select from "../../../../Components/Select";
import StepProgressBar from "../../../../Components/StepProgressBar";
import TabsComponent from "../../../../Components/Tab";
import { useAppDispatch, useAppSelector } from "../../../../Store/Hooks";
import {
  AccountTeamMember,
  CustomerTeamMember,
  clearAccountTeamCreateState,
  createAccountTeamMembersBulk,
  createCustomerTeamMember,
  createExtraSponsors,
  deactivateTeamMember,
  fetchAccountTeam,
  fetchCustomerTeam,
  fetchExecutiveTeamDetail,
  fetchOrganizationUsers,
  fetchStakeholderRelationships,
  updateTeamMember,
  ExecutiveTeamMember,
  StakeholderRelationship,
} from "../../../../Store/Reducers/AccountDetailsSlice";
import { fetchAdminUsers } from "../../../../Store/Reducers/MasterSlice";
import {
  applyTeamReassign,
  previewTeamReassign,
  type TeamViewsPerson,
} from "../../../../Store/Reducers/TeamViewsSlice";
import { fetchRlSettingConfig } from "../../../../Store/Reducers/VendorAdminSlice";
import { EMAIL_REGEX } from "../../../../Utils/Constants/RegexConstants";
import {
  ACCOUNT_TEAM_PICKER_ROLES,
  ACCOUNT_TEAM_ROLE_OPTIONS,
  ACCOUNT_TEAM_ROLES,
  CUSTOMER_TEAM_ROLE_OPTIONS,
  CUSTOMER_TEAM_ROLES,
  EXECUTIVE_PICKER_ROLES,
  getAccountTeamRoleDisplayLabel,
  normalizeCustomerTeamRole,
  normalizeRoleName,
  ROLES,
  ROLE_DISPLAY_NAMES,
} from "../../../../Utils/Constants/RoleConstants";
import { toast } from "../../../../Utils/Helper/toast";
import {
  groupCustomerStakeholdersByMeddpicc,
  resolveCustomerMemberTitle,
} from "../../../Relationship/account-relationships/relationshipUtils";
import ReassignModal from "../../../Relationship/team-views/ReassignModal";

const CUSTOMER_TEAM_MEDDPICC_ROLE_LABEL = "MEDDPICC Role";
const CUSTOMER_ROLE_NOT_APPLICABLE = "Not Applicable";
/** Max length for customer_team_members.position (Member Title). */
const CUSTOMER_MEMBER_TITLE_MAX = 125;
/** Max length for customer_team_members.language (free text). */
const CUSTOMER_MEMBER_LANGUAGE_MAX = 255;

const CUSTOMER_MEDD_BADGE_CLASS: Record<string, string> = {
  EB: "bg-[#fef3c7] text-[#92400e]",
  DM: "bg-[#e0e7ff] text-[#3730a3]",
  CH: "bg-[#d1fae5] text-[#065f46]",
  EX: "bg-[#e0e7ff] text-[#3730a3]",
  IN: "bg-[#f3f4f6] text-[#374151]",
  TB: "bg-[#dbeafe] text-[#1e40af]",
  PR: "bg-[#ecfdf5] text-[#065f46]",
  IT: "bg-[#f3e8ff] text-[#6b21a8]",
  EU: "bg-[#fce7f3] text-[#9d174d]",
};

const CUSTOMER_MEDD_BADGE_BASE_CLASS =
  "inline-flex shrink-0 items-center justify-center rounded-[10px] px-[7px] py-px text-[10px] font-semibold leading-none tracking-wide";

const customerRoleDisplay = (role: string | null | undefined) =>
  normalizeCustomerTeamRole(role) ?? CUSTOMER_ROLE_NOT_APPLICABLE;

const customerMeddRoleBadge = (role: string | null | undefined): string => {
  const normalized = normalizeCustomerTeamRole(role);
  if (!normalized || normalized === CUSTOMER_TEAM_ROLES.NA) return "-";
  const code = (
    Object.entries(CUSTOMER_TEAM_ROLES) as Array<
      [keyof typeof CUSTOMER_TEAM_ROLES, string]
    >
  ).find(([, label]) => label === normalized)?.[0];
  return code ?? "-";
};

const PANEL_CLASS = "rounded-xl border border-gray-200 bg-white p-5 md:p-6";

const teamSectionRootClass = (hideHeader: boolean) =>
  `flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_8px_0_rgba(60,72,113,0.05)] ${
    hideHeader ? "h-full min-h-0" : "min-h-[260px]"
  }`;

const teamSectionScrollClass = (
  hideHeader: boolean,
  expandedScroll = false
) => {
  if (expandedScroll) return "max-h-[60vh] overflow-y-auto";
  return hideHeader
    ? "min-h-0 flex-1 overflow-y-auto"
    : "max-h-[320px] overflow-y-auto";
};

type EditTeamTarget = {
  teamType: "account" | "customer";
  memberId: string;
  memberName: string;
  email: string;
  role: string;
  position: string;
  phone: string;
  languageOrLanguages: string;
};

const EXECUTIVE_ROLE_FILTER_OPTIONS = [
  { label: "All roles", value: "all" },
  { label: "C-Level", value: ROLES.C_LEVEL },
  { label: "Executive Sponsor", value: ROLES.EXECUTIVE_SPONSOR },
  { label: "Executive", value: ROLES.EXECUTIVE },
];

type ExtraSponsorCandidate = {
  user_id: string;
  full_name: string;
  email: string;
  role_name: string;
  grade_label: string;
  /** True if this person is already on the executive team (shown but not selectable). */
  isAlreadyAdded?: boolean;
};

type VendorStakeholder = {
  id: string;
  name: string;
  roleLabel: string;
  source: "account" | "executive";
};

const formatAccountTeamMemberRole = (roleName: string) => {
  const code =
    normalizeRoleName(roleName)?.toUpperCase() ||
    roleName?.trim().toUpperCase();
  if (!code) return "";
  const label = getAccountTeamRoleDisplayLabel(code);
  return label !== code ? `${code} (${label})` : code;
};

const formatExecutiveTeamMemberRole = (
  member: ExecutiveTeamMember,
  variant: "assigned" | "additional"
) => {
  if (variant === "additional") return "Additional Executive";
  const executiveType = member.executive_type?.toLowerCase();
  if (executiveType === "primary") return "Primary Sponsor";
  if (executiveType === "secondary") return "Secondary Sponsor";
  return formatAccountTeamMemberRole(member.role_name) || "Executive Sponsor";
};

/** Synthetic Team Views person for workspace unlink → Reassign / Remove modal. */
function buildWorkspaceReassignPerson(opts: {
  userId: string;
  fullName: string;
  accountId: string;
  accountName: string;
  roleName: string;
  roleKey: string;
}): TeamViewsPerson {
  return {
    user_id: opts.userId,
    full_name: opts.fullName,
    account_count: 1,
    reassign_eligible: true,
    assignments: [
      {
        account_id: opts.accountId,
        account_name: opts.accountName,
        role_name: opts.roleName,
        role_key: opts.roleKey,
        reassign_eligible: true,
      },
    ],
  };
}

function accountTeamRoleKey(roleName?: string | null): string {
  return (
    normalizeRoleName(roleName || "")?.toUpperCase() ||
    (roleName || "").trim().toUpperCase() ||
    "Team"
  );
}

function extraSponsorReassignRole(member: ExecutiveTeamMember): {
  roleName: string;
  roleKey: string;
} {
  const slug = normalizeRoleName(member.role_name || "");
  if (slug === ROLES.EXECUTIVE) {
    return { roleName: "Non-sponsor Exec", roleKey: "NON_SPONSOR_EXEC" };
  }
  return { roleName: "Additional Executive", roleKey: "EXTRA_SPONSOR" };
}

const TEAM_MEMBER_NAME_CLASS = "truncate text-sm font-semibold text-gray-900";
const TEAM_MEMBER_POSITION_CLASS =
  "mt-0.5 truncate text-xs font-normal text-gray-500";
const TEAM_MEMBER_ROLE_CLASS =
  "mt-0.5 truncate text-xs font-semibold text-[#974ae4]";
const TEAM_MEMBER_EMAIL_CLASS =
  "mt-0.5 truncate text-xs font-normal text-gray-400";

const TeamMemberInfoLines = ({
  name,
  position,
  role,
  email,
}: {
  name: string;
  position?: string | null;
  role?: string | null;
  email?: string | null;
}) => (
  <>
    <p className={TEAM_MEMBER_NAME_CLASS}>{name}</p>
    {position?.trim() ? (
      <p className={TEAM_MEMBER_POSITION_CLASS}>{position.trim()}</p>
    ) : null}
    {role?.trim() ? (
      <p className={TEAM_MEMBER_ROLE_CLASS}>{role.trim()}</p>
    ) : null}
    {email?.trim() ? (
      <p className={TEAM_MEMBER_EMAIL_CLASS}>{email.trim()}</p>
    ) : null}
  </>
);

const CustomerTeamMemberInfoLines = ({
  name,
  position,
  role,
  email,
}: {
  name: string;
  position?: string | null;
  role?: string | null;
  email?: string | null;
}) => {
  const badge = customerMeddRoleBadge(role);
  const roleLabel = customerRoleDisplay(role);

  return (
    <>
      <div className="flex min-w-0 items-center gap-1.5">
        <p className="min-w-0 truncate text-sm font-semibold leading-5 text-gray-900">
          {name}
        </p>
        {badge !== "-" ? (
          <span
            className={`${CUSTOMER_MEDD_BADGE_BASE_CLASS} ${
              CUSTOMER_MEDD_BADGE_CLASS[badge] ?? "bg-gray-100 text-gray-600"
            }`}
            title={roleLabel}
          >
            {badge}
          </span>
        ) : null}
      </div>
      {position?.trim() ? (
        <p className={TEAM_MEMBER_POSITION_CLASS}>{position.trim()}</p>
      ) : null}
      {email?.trim() ? (
        <p className={TEAM_MEMBER_EMAIL_CLASS}>{email.trim()}</p>
      ) : null}
    </>
  );
};

interface TeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountName: string;
  teamType: "account" | "customer";
  mode?: "add" | "edit";
  initialData?: EditTeamTarget | null;
  approverOptions: { label: string; value: string }[];
  loading: boolean;
  submitError: string | null;
  onSubmitAccount: (payload: {
    batch_name: string | null;
    approver_id: string;
    members: {
      email: string;
      full_name: string;
      phone: string;
      position: string;
      role: string;
      grade: number;
      languages: string;
    }[];
  }) => Promise<boolean>;
  onSubmitCustomer: (payload: {
    name: string;
    email: string;
    phone: string;
    role: string;
    position: string;
    language: string;
  }) => Promise<boolean>;
  onUpdate?: (payload: {
    memberId: string;
    teamType: "account" | "customer";
    body: Record<string, any>;
  }) => Promise<boolean>;
}

const TeamMemberModal = ({
  isOpen,
  onClose,
  accountName,
  teamType,
  mode = "add",
  initialData = null,
  approverOptions,
  loading,
  submitError,
  onSubmitAccount,
  onSubmitCustomer,
  onUpdate,
}: TeamMemberModalProps) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [memberName, setMemberName] = useState("");
  const [position, setPosition] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [language, setLanguage] = useState("");
  const [role, setRole] = useState("");
  const [batchName, setBatchName] = useState("");
  const [approverId, setApproverId] = useState("");

  const isAccountTeam = teamType === "account";
  const usesApprovalFlow = isAccountTeam;
  const teamLabel = isAccountTeam ? "Account" : "Customer";
  const defaultBatchName = isAccountTeam
    ? "In-app account team add"
    : "In-app customer team add";

  useEffect(() => {
    if (!isOpen) return;

    setStep(1);
    if (mode === "edit" && initialData) {
      setMemberName(initialData.memberName || "");
      setPosition(initialData.position || "");
      setEmail(initialData.email || "");
      setPhone(initialData.phone || "");
      setLanguage(initialData.languageOrLanguages || "");
      setRole(
        initialData.role ||
          (isAccountTeam ? ACCOUNT_TEAM_ROLES.AE : CUSTOMER_TEAM_ROLES.NA)
      );
      setBatchName(
        isAccountTeam ? "Edit account team member" : "Edit customer team member"
      );
    } else {
      setMemberName("");
      setPosition("");
      setEmail("");
      setPhone("");
      setLanguage("");
      setRole(isAccountTeam ? ACCOUNT_TEAM_ROLES.AE : CUSTOMER_TEAM_ROLES.NA);
      setBatchName(defaultBatchName);
    }

    setApproverId(approverOptions[0]?.value || "");
  }, [
    isOpen,
    mode,
    initialData,
    approverOptions,
    isAccountTeam,
    defaultBatchName,
  ]);

  const emailError = useMemo(() => {
    if (!email.trim()) return "";
    return EMAIL_REGEX.test(email.trim())
      ? ""
      : "Please enter a valid email address";
  }, [email]);

  const positionError = useMemo(() => {
    if (isAccountTeam) return "";
    if (position.length > CUSTOMER_MEMBER_TITLE_MAX) {
      return `Member Title must be at most ${CUSTOMER_MEMBER_TITLE_MAX} characters`;
    }
    return "";
  }, [isAccountTeam, position]);

  const languageError = useMemo(() => {
    if (isAccountTeam) return "";
    if (language.length > CUSTOMER_MEMBER_LANGUAGE_MAX) {
      return `Language must be at most ${CUSTOMER_MEMBER_LANGUAGE_MAX} characters`;
    }
    return "";
  }, [isAccountTeam, language]);

  const customerRoleOptions = useMemo(() => {
    if (!role || CUSTOMER_TEAM_ROLE_OPTIONS.some(o => o.value === role)) {
      return CUSTOMER_TEAM_ROLE_OPTIONS;
    }
    return [{ label: role, value: role }, ...CUSTOMER_TEAM_ROLE_OPTIONS];
  }, [role]);

  const canReview = useMemo(
    () =>
      memberName.trim() &&
      position.trim() &&
      email.trim() &&
      !emailError &&
      !positionError &&
      !languageError &&
      role.trim(),
    [
      memberName,
      position,
      email,
      emailError,
      positionError,
      languageError,
      role,
    ]
  );

  const handleSubmit = async () => {
    if (loading) return;
    if (usesApprovalFlow && !approverId) return;
    if (!isAccountTeam && (positionError || languageError)) return;

    let ok = false;
    if (mode === "edit" && initialData && onUpdate) {
      if (isAccountTeam) {
        ok = await onUpdate({
          memberId: initialData.memberId,
          teamType,
          body: {
            batch_name: batchName.trim() || null,
            approver_id: approverId,
            grade: 2,
            position: position.trim() || null,
            phone: phone.trim() || null,
            full_name: memberName.trim() || null,
            languages: language.trim() || "",
          },
        });
      } else {
        ok = await onUpdate({
          memberId: initialData.memberId,
          teamType,
          body: {
            name: memberName.trim() || null,
            phone: phone.trim() || null,
            position: position.trim() || null,
            role: role.trim() || "",
            language: language.trim() || "",
          },
        });
      }
    } else if (isAccountTeam) {
      ok = await onSubmitAccount({
        batch_name: batchName.trim() || null,
        approver_id: approverId,
        members: [
          {
            email: email.trim(),
            full_name: memberName.trim(),
            phone: phone.trim(),
            position: position.trim(),
            role,
            grade: 1,
            languages: language.trim(),
          },
        ],
      });
    } else {
      ok = await onSubmitCustomer({
        name: memberName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        role: role.trim(),
        position: position.trim(),
        language: language.trim(),
      });
    }

    if (ok) onClose();
  };

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      onCloseButton={onClose}
      title={`${mode === "edit" ? "Edit" : "Add"} ${teamLabel} Team Member`}
      size="3xl"
      showHeader
      contentClassName="overflow-y-auto p-0"
      className="max-h-[92vh]"
      nested
    >
      {usesApprovalFlow ? (
        <div className="border-b border-gray-200 px-5 py-3">
          <StepProgressBar
            steps={[
              { label: "Details" },
              { label: "Review & Select Approver" },
            ]}
            currentStepIndex={step}
          />
        </div>
      ) : null}

      <div className="space-y-5 px-5 py-5">
        {!usesApprovalFlow || step === 1 ? (
          <>
            <CommonInput
              label="Account"
              name="account"
              value={accountName}
              onChange={() => undefined}
              disabled
            />
            <CommonInput
              label="Member Name"
              name="member_name"
              required
              value={memberName}
              onChange={e => setMemberName(e.target.value)}
              placeholder="Jane Doe"
            />
            <CommonInput
              label="Member Title"
              name="position"
              required
              value={position}
              onChange={e => setPosition(e.target.value)}
              placeholder={
                isAccountTeam ? "Senior Account Executive" : "Customer Contact"
              }
              error={!isAccountTeam ? positionError : undefined}
            />
            <CommonInput
              label="Member Email"
              name="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="jane.doe@company.com"
              error={emailError}
              disabled={mode === "edit"}
              infoText={mode === "edit" ? "Email cannot be edited" : undefined}
            />
            <CommonInput
              label="Member Phone"
              name="phone"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+1-555-0100"
            />
            <CommonInput
              label={isAccountTeam ? "Languages" : "Language"}
              name="language"
              value={language}
              onChange={e => setLanguage(e.target.value)}
              placeholder="English, Spanish"
              maxLength={
                isAccountTeam ? undefined : CUSTOMER_MEMBER_LANGUAGE_MAX
              }
              error={!isAccountTeam ? languageError : undefined}
            />
            {isAccountTeam ? (
              <Select
                label="Role"
                name="role"
                required
                value={role}
                options={ACCOUNT_TEAM_ROLE_OPTIONS}
                onChange={e => setRole(String(e.target.value))}
                isShowPlaceHolder={false}
                infoText={`One of: ${ACCOUNT_TEAM_ROLE_OPTIONS.map(o => o.label).join(", ")}`}
                disabled={mode === "edit"}
              />
            ) : (
              <Select
                label={CUSTOMER_TEAM_MEDDPICC_ROLE_LABEL}
                name="role"
                required
                value={role}
                options={customerRoleOptions}
                onChange={e => setRole(String(e.target.value))}
                isShowPlaceHolder={false}
                infoText={`One of: ${CUSTOMER_TEAM_ROLE_OPTIONS.map(o => o.label).join(", ")}`}
              />
            )}
            {submitError && !usesApprovalFlow ? (
              <p className="text-sm font-medium text-red-600">{submitError}</p>
            ) : null}
          </>
        ) : (
          <>
            <div>
              <h4 className="mb-3 text-[0.9375rem] font-semibold text-gray-800">
                Review member
              </h4>
              <div className="overflow-hidden rounded-lg border border-gray-200">
                {[
                  { key: "Account", value: accountName },
                  { key: "Member Name", value: memberName || "-" },
                  { key: "Title", value: position || "-" },
                  { key: "Email", value: email || "-" },
                  { key: "Phone", value: phone || "-" },
                  {
                    key: isAccountTeam ? "Languages" : "Language",
                    value: language || "-",
                  },
                  {
                    key: isAccountTeam
                      ? "Role"
                      : CUSTOMER_TEAM_MEDDPICC_ROLE_LABEL,
                    value: role || "-",
                  },
                ].map((row, index) => (
                  <div
                    key={row.key}
                    className={`grid grid-cols-[160px_1fr] gap-2 px-4 py-2.5 ${
                      index < 6 ? "border-b border-gray-200" : ""
                    }`}
                  >
                    <span className="text-sm font-semibold text-gray-500">
                      {row.key}
                    </span>
                    <span className="text-sm text-gray-900">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <CommonInput
                className="mb-4"
                label="Batch name (optional)"
                name="batch_name"
                value={batchName}
                onChange={e => setBatchName(e.target.value)}
                placeholder={defaultBatchName}
              />
              <Select
                label="Select Approver"
                name="approver_id"
                required
                value={approverId}
                onChange={e => setApproverId(String(e.target.value))}
                options={approverOptions}
                disabled={approverOptions.length === 0}
                isShowPlaceHolder={false}
              />
              <p className="mt-2 text-[13px] text-amber-700">
                This member will be sent for approval before being added.
              </p>
              {/* {submitError ? (
                <p className="mt-2 text-sm font-medium text-red-600">
                  {submitError}
                </p>
              ) : null} */}
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4">
        {!usesApprovalFlow ? (
          <>
            <Button
              variant="outline-primary"
              className="inline-flex items-center gap-2"
              onClick={onClose}
            >
              <MoveLeft size={16} />
              Cancel
            </Button>
            <Button
              variant="primary"
              className="inline-flex items-center gap-2"
              onClick={handleSubmit}
              disabled={loading || !canReview}
            >
              {loading ? "Saving..." : mode === "edit" ? "Save" : "Add"}
              {!loading ? <MoveRight size={16} /> : null}
            </Button>
          </>
        ) : step === 1 ? (
          <>
            <Button
              variant="outline-primary"
              className="inline-flex items-center gap-2"
              onClick={onClose}
            >
              <MoveLeft size={16} />
              Cancel
            </Button>
            <Button
              variant="primary"
              className="inline-flex items-center gap-2"
              onClick={() => setStep(2)}
              disabled={!canReview}
            >
              Review
              <MoveRight size={16} />
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline-primary"
              className="inline-flex items-center gap-2"
              onClick={() => setStep(1)}
            >
              <MoveLeft size={16} />
              Back
            </Button>
            <Button
              variant="primary"
              className="inline-flex items-center gap-2"
              onClick={handleSubmit}
              disabled={loading || !approverId}
            >
              {loading ? "Submitting..." : "Confirm"}
              {!loading ? <MoveRight size={16} /> : null}
            </Button>
          </>
        )}
      </div>
    </CommonModal>
  );
};

interface AddExecutiveTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidates: ExtraSponsorCandidate[];
  loading: boolean;
  submitError: string | null;
  onSubmit: (
    members: { user_id: string; reason: string }[]
  ) => Promise<boolean>;
}

const AddExecutiveTeamModal = ({
  isOpen,
  onClose,
  candidates,
  loading,
  submitError,
  onSubmit,
}: AddExecutiveTeamModalProps) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [reasons, setReasons] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setSearch("");
    setRoleFilter("all");
    setSelectedIds([]);
    setReasons({});
  }, [isOpen]);

  const filteredCandidates = useMemo(() => {
    const text = search.trim().toLowerCase();
    return candidates.filter(candidate => {
      const role = normalizeRoleName(candidate.role_name) || "";
      const roleMatch = roleFilter === "all" || role === roleFilter;
      const searchMatch =
        !text ||
        candidate.full_name.toLowerCase().includes(text) ||
        candidate.email.toLowerCase().includes(text);
      return roleMatch && searchMatch;
    });
  }, [candidates, roleFilter, search]);

  const selectedCandidates = useMemo(
    () =>
      candidates.filter(candidate => selectedIds.includes(candidate.user_id)),
    [candidates, selectedIds]
  );

  const canContinue = selectedIds.length > 0;
  const hasReasonErrors = selectedCandidates.some(
    candidate => !(reasons[candidate.user_id] || "").trim()
  );

  const handleToggleCandidate = (userId: string) => {
    setSelectedIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSubmit = async () => {
    if (hasReasonErrors || loading) return;
    const ok = await onSubmit(
      selectedCandidates.map(candidate => ({
        user_id: candidate.user_id,
        reason: (reasons[candidate.user_id] || "").trim(),
      }))
    );
    if (ok) onClose();
  };

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      onCloseButton={onClose}
      title={step === 1 ? "Add Executive" : "Add Reason"}
      size="2xl"
      showHeader
      contentClassName="overflow-y-auto p-0"
      nested
    >
      <div className="border-b border-gray-200 px-5 py-3">
        <StepProgressBar
          steps={[{ label: "Select Executives" }, { label: "Add Reason" }]}
          currentStepIndex={step}
        />
      </div>

      <div className="space-y-4 px-5 py-4">
        {step === 1 ? (
          <>
            <CommonInput
              name="search-executive"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name..."
            />
            <div className="w-[220px]">
              <Select
                name="role-filter"
                value={roleFilter}
                options={EXECUTIVE_ROLE_FILTER_OPTIONS}
                onChange={e => setRoleFilter(String(e.target.value))}
                isShowPlaceHolder={false}
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto rounded-lg border border-gray-200">
              {filteredCandidates.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  No executives found
                </div>
              ) : (
                filteredCandidates.map(candidate => (
                  <label
                    key={candidate.user_id}
                    className={`flex items-start gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 ${
                      candidate.isAlreadyAdded
                        ? "cursor-not-allowed bg-amber-50"
                        : "cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(candidate.user_id)}
                      disabled={candidate.isAlreadyAdded}
                      onChange={() => {
                        if (candidate.isAlreadyAdded) return;
                        handleToggleCandidate(candidate.user_id);
                      }}
                      className="mt-1 h-4 w-4 rounded border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p
                          className={`mb-0 truncate text-[15px] font-semibold ${
                            candidate.isAlreadyAdded
                              ? "text-gray-500"
                              : "text-gray-900"
                          }`}
                        >
                          {candidate.full_name}
                        </p>
                        {candidate.isAlreadyAdded && (
                          <span className="shrink-0 rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                            Already Added
                          </span>
                        )}
                      </div>
                      <p
                        className={`mb-1 truncate text-sm ${
                          candidate.isAlreadyAdded
                            ? "text-gray-400"
                            : "text-gray-500"
                        }`}
                      >
                        {candidate.email}
                      </p>
                      <div className="flex items-center gap-2">
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${
                            candidate.isAlreadyAdded
                              ? "bg-gray-100 text-gray-500"
                              : "bg-violet-100 text-violet-700"
                          }`}
                        >
                          {ROLE_DISPLAY_NAMES[
                            (normalizeRoleName(candidate.role_name) ||
                              ROLES.EXECUTIVE_SPONSOR) as keyof typeof ROLE_DISPLAY_NAMES
                          ] || candidate.role_name}
                        </span>
                        <span
                          className={`rounded px-2 py-0.5 text-xs ${
                            candidate.isAlreadyAdded
                              ? "bg-gray-50 text-gray-400"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {candidate.grade_label}
                        </span>
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <p className="mb-0 text-[15px] font-semibold text-gray-800">
              Enter a reason for each executive.
            </p>
            <div className="max-h-[370px] space-y-3 overflow-y-auto pr-1">
              {selectedCandidates.map(candidate => (
                <div
                  key={candidate.user_id}
                  className="rounded-lg border border-gray-200 p-3"
                >
                  <p className="mb-0 text-base font-semibold text-gray-900">
                    {candidate.full_name}
                  </p>
                  <p className="mb-2 text-sm text-gray-500">
                    {candidate.email}
                  </p>
                  <CommonInput
                    name={`reason-${candidate.user_id}`}
                    value={reasons[candidate.user_id] || ""}
                    onChange={e =>
                      setReasons(prev => ({
                        ...prev,
                        [candidate.user_id]: e.target.value,
                      }))
                    }
                    placeholder="Enter reason for adding this executive..."
                    maxLength={50}
                    error={
                      (reasons[candidate.user_id] || "").trim()
                        ? ""
                        : "Reason is required"
                    }
                  />
                </div>
              ))}
            </div>
          </>
        )}
        {submitError ? (
          <p className="text-sm font-medium text-red-600">{submitError}</p>
        ) : null}
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-5 py-4">
        {step === 1 ? (
          <>
            <Button variant="outline-primary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => setStep(2)}
              disabled={!canContinue}
            >
              Next: Add Reason
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-primary" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading || hasReasonErrors}
            >
              {loading ? "Adding..." : "Add Executives"}
            </Button>
          </>
        )}
      </div>
    </CommonModal>
  );
};

interface ExecutiveTeamSectionProps {
  sponsors: ExecutiveTeamMember[];
  extraSponsors: ExecutiveTeamMember[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onAdd: () => void;
  onUnlink: (member: ExecutiveTeamMember) => void;
  unlinkingUserId: string | null;
  hideHeader?: boolean;
  expandedScroll?: boolean;
}

const ExecutiveTeamSection = ({
  sponsors,
  extraSponsors,
  loading,
  error,
  onRetry,
  onAdd,
  onUnlink,
  unlinkingUserId,
  hideHeader = false,
  expandedScroll = false,
}: ExecutiveTeamSectionProps) => {
  const primarySecondary = sponsors.filter(
    member =>
      member.executive_type?.toLowerCase() === "primary" ||
      member.executive_type?.toLowerCase() === "secondary"
  );

  return (
    <div className={teamSectionRootClass(hideHeader)}>
      {!hideHeader ? (
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-white px-4 py-3.5">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-violet-50 text-[#974ae4]">
              <Award size={16} strokeWidth={2} />
            </span>
            <h3 className="text-sm font-semibold text-gray-900">
              Executive Team
            </h3>
          </div>
          <Button
            variant="primary"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium"
            onClick={onAdd}
          >
            <Plus size={14} />
            Add
          </Button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex flex-1 items-center justify-center px-4 py-8">
          <p className="text-sm text-gray-500">Loading executive team...</p>
        </div>
      ) : error ? (
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-8 text-center">
          <p className="text-sm font-medium text-red-600">{error}</p>
          <Button variant="outline-primary" className="mt-3" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : primarySecondary.length === 0 && extraSponsors.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center bg-gradient-to-b from-gray-50/40 to-gray-50 px-4 py-8">
          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-gray-200 bg-white text-gray-400">
            <Award size={22} strokeWidth={1.5} />
          </div>
          <p className="text-sm font-medium text-gray-600">
            No team members yet
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Add members to build this team
          </p>
        </div>
      ) : (
        <div className={teamSectionScrollClass(hideHeader, expandedScroll)}>
          {primarySecondary.map((member, index) => (
            <div
              key={`${member.user_id}-${member.executive_type || index}`}
              className={`flex items-start justify-between gap-3 px-4 py-3.5 ${
                index < primarySecondary.length - 1 || extraSponsors.length > 0
                  ? "border-b border-gray-100"
                  : ""
              }`}
            >
              <div className="min-w-0 flex-1">
                <TeamMemberInfoLines
                  name={member.full_name}
                  position={member.position}
                  role={formatExecutiveTeamMemberRole(member, "assigned")}
                  email={member.email}
                />
              </div>
            </div>
          ))}
          {extraSponsors.map((member, index) => (
            <div
              key={member.id || member.user_id}
              className={`flex items-start justify-between gap-3 px-4 py-3.5 ${index < extraSponsors.length - 1 ? "border-b border-gray-100" : ""}`}
            >
              <div className="min-w-0 flex-1">
                <TeamMemberInfoLines
                  name={member.full_name}
                  position={member.position || member.reason || ""}
                  role={formatExecutiveTeamMemberRole(member, "additional")}
                  email={member.email}
                />
              </div>
              <Button
                variant="clear"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md p-0 hover:bg-red-50"
                aria-label={`Unlink ${member.full_name}`}
                disabled={unlinkingUserId === member.user_id}
                onClick={() => onUnlink(member)}
              >
                <Unlink2 size={18} className="text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface AccountTeamSectionProps {
  members: AccountTeamMember[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onAdd: () => void;
  onUnlink: (member: AccountTeamMember) => void;
  onEdit: (target: EditTeamTarget) => void;
  unlinkingUserId: string | null;
  hideHeader?: boolean;
  expandedScroll?: boolean;
}

const AccountTeamSection = ({
  members,
  loading,
  error,
  onRetry,
  onAdd,
  onUnlink,
  onEdit,
  unlinkingUserId,
  hideHeader = false,
  expandedScroll = false,
}: AccountTeamSectionProps) => (
  <div className={teamSectionRootClass(hideHeader)}>
    {!hideHeader ? (
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-white px-4 py-3.5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-violet-50 text-[#974ae4]">
            <Users size={16} strokeWidth={2} />
          </span>
          <h3 className="text-sm font-semibold text-gray-900">Account Team</h3>
        </div>
        <Button
          variant="primary"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium"
          onClick={onAdd}
        >
          <Plus size={14} />
          Add
        </Button>
      </div>
    ) : null}

    {loading ? (
      <div className="flex flex-1 items-center justify-center px-4 py-8">
        <p className="text-sm text-gray-500">Loading account team...</p>
      </div>
    ) : error ? (
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-8 text-center">
        <p className="text-sm font-medium text-red-600">{error}</p>
        <Button variant="outline-primary" className="mt-3" onClick={onRetry}>
          Try again
        </Button>
      </div>
    ) : members.length === 0 ? (
      <div className="flex flex-1 flex-col items-center justify-center bg-gradient-to-b from-gray-50/40 to-gray-50 px-4 py-8">
        <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-gray-200 bg-white text-gray-400">
          <Users size={22} strokeWidth={1.5} />
        </div>
        <p className="text-sm font-medium text-gray-600">No team members yet</p>
        <p className="mt-1 text-xs text-gray-400">
          Add members to build this team
        </p>
      </div>
    ) : (
      <div className={teamSectionScrollClass(hideHeader, expandedScroll)}>
        {members.map((member, index) => (
          <div
            key={member.id}
            className={`flex items-start justify-between gap-3 px-4 py-3.5 ${index < members.length - 1 ? "border-b border-gray-100" : ""}`}
          >
            <div className="min-w-0 flex-1">
              <TeamMemberInfoLines
                name={member.full_name}
                position={member.position}
                role={formatAccountTeamMemberRole(member.role_name)}
                email={member.email}
              />
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="clear"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md p-0 hover:bg-blue-50"
                aria-label={`Edit ${member.full_name}`}
                onClick={() =>
                  onEdit({
                    teamType: "account",
                    memberId: member.user_id,
                    memberName: member.full_name,
                    email: member.email,
                    role: member.role_name,
                    position: member.position || "",
                    phone: member.phone || "",
                    languageOrLanguages: "",
                  })
                }
              >
                <SquarePen size={18} className="text-blue-600" />
              </Button>
              <Button
                variant="clear"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md p-0 hover:bg-red-50"
                aria-label={`Unlink ${member.full_name}`}
                onClick={() => onUnlink(member)}
                disabled={unlinkingUserId === member.user_id}
              >
                <Unlink2 size={18} className="text-red-500" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

interface CustomerTeamSectionProps {
  members: CustomerTeamMember[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onAdd: () => void;
  onEdit: (target: EditTeamTarget) => void;
  onUnlink: (member: CustomerTeamMember) => void;
  unlinkingUserId: string | null;
  hideHeader?: boolean;
  expandedScroll?: boolean;
}

const CustomerTeamSection = ({
  members,
  loading,
  error,
  onRetry,
  onAdd,
  onEdit,
  onUnlink,
  unlinkingUserId,
  hideHeader = false,
  expandedScroll = false,
}: CustomerTeamSectionProps) => {
  const dispatch = useAppDispatch();
  const showMeddpicc = useAppSelector(
    state => state.vendorAdmin.rlSettingValue?.consider_meddpicc_role === true
  );

  useEffect(() => {
    dispatch(fetchRlSettingConfig());
  }, [dispatch]);

  const sections = useMemo(
    () =>
      groupCustomerStakeholdersByMeddpicc(
        members.map(m => ({
          ...m,
          member_name: m.name,
          meddpicc_role: m.role,
        })),
        { considerMeddpiccRole: showMeddpicc }
      ),
    [members, showMeddpicc]
  );

  return (
    <div className={teamSectionRootClass(hideHeader)}>
      {!hideHeader ? (
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-white px-4 py-3.5">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-violet-50 text-[#974ae4]">
              <UsersRound size={16} strokeWidth={2} />
            </span>
            <h3 className="text-sm font-semibold text-gray-900">
              Customer Team
            </h3>
          </div>
          <Button
            variant="primary"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium"
            onClick={onAdd}
          >
            <Plus size={14} />
            Add
          </Button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex flex-1 items-center justify-center px-4 py-8">
          <p className="text-sm text-gray-500">Loading customer team...</p>
        </div>
      ) : error ? (
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-8 text-center">
          <p className="text-sm font-medium text-red-600">{error}</p>
          <Button variant="outline-primary" className="mt-3" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : members.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center bg-gradient-to-b from-gray-50/40 to-gray-50 px-4 py-8">
          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-gray-200 bg-white text-gray-400">
            <UsersRound size={22} strokeWidth={1.5} />
          </div>
          <p className="text-sm font-medium text-gray-600">
            No team members yet
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Add members to build this team
          </p>
        </div>
      ) : (
        <div className={teamSectionScrollClass(hideHeader, expandedScroll)}>
          {sections.map(section => (
            <React.Fragment key={section.group ?? "all"}>
              {section.label ? (
                <div className="px-4 pt-3 pb-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {section.label === "Unknown"
                      ? CUSTOMER_ROLE_NOT_APPLICABLE
                      : section.label}
                  </p>
                </div>
              ) : null}
              {section.items.map(
                (member: CustomerTeamMember, index: number) => {
                  const mapped = {
                    ...member,
                    member_name: member.name,
                    meddpicc_role: member.role,
                  };
                  const memberTitle = resolveCustomerMemberTitle(mapped);
                  const position = member.position?.trim() || memberTitle;

                  return (
                    <div
                      key={member.user_id}
                      className={`flex items-start justify-between gap-3 px-4 py-3.5 ${index < section.items.length - 1 ? "border-b border-gray-100" : ""}`}
                    >
                      <div className="min-w-0 flex-1">
                        <CustomerTeamMemberInfoLines
                          name={member.name}
                          position={position}
                          role={member.role}
                          email={member.email}
                        />
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="clear"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md p-0 hover:bg-blue-50"
                          aria-label={`Edit ${member.name}`}
                          onClick={() =>
                            onEdit({
                              teamType: "customer",
                              memberId: member.user_id,
                              memberName: member.name,
                              email: member.email,
                              role: member.role || "",
                              position: member.position || "",
                              phone: member.phone || "",
                              languageOrLanguages: member.language || "",
                            })
                          }
                        >
                          <SquarePen size={18} className="text-blue-600" />
                        </Button>
                        <Button
                          variant="clear"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md p-0 hover:bg-red-50"
                          aria-label={`Unlink ${member.name}`}
                          onClick={() => onUnlink(member)}
                          disabled={unlinkingUserId === member.user_id}
                        >
                          <Unlink2 size={18} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                }
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

interface StakeholderMappingSectionProps {
  vendorStakeholders: VendorStakeholder[];
  customerStakeholders: CustomerTeamMember[];
  relationships: StakeholderRelationship[];
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;
  onSave: (
    links: {
      team_type: "account_team_member" | "executive_team_member";
      account_team_member?: string;
      executive_team_member?: string;
      customer_team_member: string;
      relationship_percentage: number | null;
      is_priority: boolean;
    }[]
  ) => Promise<boolean>;
  variant?: "panel" | "card";
  className?: string;
}

export const StakeholderMappingSection = ({
  vendorStakeholders,
  customerStakeholders,
  relationships,
  loading,
  error,
  saving,
  saveError,
  onSave,
  variant = "panel",
  className = "",
}: StakeholderMappingSectionProps) => {
  const relationshipKeys = useMemo<Set<string>>(() => {
    const keys = new Set<string>();

    relationships.forEach(item => {
      if (item.active === false) return;
      const customerId = item.customer_team_member;
      const vendorId =
        item.account_team_member ||
        item.executive_team_member ||
        (item as any).vendor_stakeholder;

      if (vendorId && customerId) {
        keys.add(`${vendorId}::${customerId}`);
      }
    });

    return keys;
  }, [relationships]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [draftKeys, setDraftKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isEditMode) {
      setDraftKeys(new Set(relationshipKeys));
    }
  }, [relationshipKeys, isEditMode]);

  const activeKeys = isEditMode ? draftKeys : relationshipKeys;

  const toggleDraftCell = (key: string) => {
    if (!isEditMode) return;
    setDraftKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCancelEdit = () => {
    setDraftKeys(new Set(relationshipKeys));
    setIsEditMode(false);
  };

  const handleSaveEdit = async () => {
    const links: {
      team_type: "account_team_member" | "executive_team_member";
      account_team_member?: string;
      executive_team_member?: string;
      customer_team_member: string;
      relationship_percentage: number | null;
      is_priority: boolean;
    }[] = [];

    vendorStakeholders.forEach(vendor => {
      customerStakeholders.forEach(customer => {
        const key = `${vendor.id}::${customer.user_id}`;
        if (!draftKeys.has(key)) return;

        if (vendor.source === "account") {
          links.push({
            team_type: "account_team_member",
            account_team_member: vendor.id,
            customer_team_member: customer.user_id,
            relationship_percentage: null,
            is_priority: true,
          });
        } else {
          links.push({
            team_type: "executive_team_member",
            executive_team_member: vendor.id,
            customer_team_member: customer.user_id,
            relationship_percentage: null,
            is_priority: true,
          });
        }
      });
    });

    const ok = await onSave(links);
    if (ok) setIsEditMode(false);
  };

  const isCard = variant === "card";

  const headerBlock = (
    <>
      <div
        className={
          isCard
            ? "flex shrink-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
            : "mb-4 flex items-start justify-between gap-3"
        }
      >
        <div className="min-w-0 flex-1">
          {isCard ? (
            <h2 className="m-0 text-base font-bold leading-6 text-black">
              Stakeholders Mapping
            </h2>
          ) : (
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
              Stakeholder Mapping
            </h2>
          )}
          <p
            className={
              isCard
                ? "m-0 mt-1 text-xs leading-relaxed text-gray-500"
                : "mt-1 text-xs text-gray-500"
            }
          >
            {isCard
              ? "Map vendor team members to customer contacts"
              : "Vendor stakeholders vs customer stakeholders relationship matrix"}
          </p>
        </div>
        {isEditMode ? (
          <div
            className={
              isCard
                ? "flex shrink-0 flex-wrap items-center justify-end gap-2"
                : "flex items-center gap-2"
            }
          >
            <Button
              variant="clear"
              className="text-xs font-medium"
              onClick={handleCancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="px-4 py-2 text-sm font-medium"
              onClick={handleSaveEdit}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Stakeholder Mapping"}
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            className="inline-flex shrink-0 items-center gap-2 self-start px-4 py-2 text-sm font-medium sm:self-auto"
            onClick={() => {
              setDraftKeys(new Set(relationshipKeys));
              setIsEditMode(true);
            }}
          >
            <SquarePen size={14} />
            Edit Stakeholder Mapping
          </Button>
        )}
      </div>
      {isCard ? (
        <hr className="m-0 mb-3 mt-0 w-full shrink-0 border-0 border-t border-[#F6F6F6]" />
      ) : null}
    </>
  );

  const bodyContent = (
    <>
      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm text-gray-500">
          Loading stakeholder mappings...
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
          {error}
        </div>
      ) : vendorStakeholders.length === 0 ||
        customerStakeholders.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm text-gray-500">
          Add vendor and customer stakeholders to view mapping.
        </div>
      ) : (
        <div className="space-y-2">
          {saveError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
              {saveError}
            </div>
          ) : null}
          <div
            className={
              isCard
                ? "max-h-[min(22rem,55vh)] overflow-auto rounded-lg border border-gray-200 bg-white shadow-[inset_-8px_0_12px_-8px_rgba(0,0,0,0.06)]"
                : "overflow-x-auto rounded-lg border border-gray-200 bg-white"
            }
          >
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th
                    rowSpan={2}
                    className={`sticky left-0 z-30 border-b border-r border-gray-200 bg-gray-50 align-middle text-left text-xs font-semibold uppercase tracking-wide text-[#974ae4] ${
                      isCard
                        ? "w-[min(11rem,28vw)] min-w-[9rem] px-2.5 py-2.5"
                        : "w-[270px] px-3 py-3"
                    }`}
                  >
                    <span className="block text-left text-xs font-semibold uppercase leading-tight tracking-wide text-[#974ae4]">
                      Vendor
                      <br />
                      Stakeholders
                    </span>
                  </th>
                  <th
                    colSpan={customerStakeholders.length}
                    className={`border-b border-r border-gray-200 text-center text-xs font-semibold uppercase tracking-wide text-[#974ae4] ${
                      isCard ? "px-2 py-2" : "px-3 py-3"
                    }`}
                  >
                    Customer Stakeholders
                  </th>
                </tr>
                <tr className="bg-gray-50">
                  {customerStakeholders.map(member => (
                    <th
                      key={member.user_id}
                      className={`border-b border-r border-gray-200 text-center ${
                        isCard
                          ? "min-w-[7.5rem] max-w-[10rem] px-2 py-2"
                          : "min-w-[200px] px-3 py-3"
                      }`}
                    >
                      {(() => {
                        const customerTitle =
                          member.role && member.position
                            ? `${member.role} (${member.position})`
                            : member.role || member.position || "-";
                        return (
                          <>
                            <p
                              className={`line-clamp-2 text-center font-semibold text-gray-900 ${
                                isCard ? "text-xs leading-tight" : "text-sm"
                              }`}
                              title={member.name}
                            >
                              {member.name}
                            </p>
                            <p
                              className={`line-clamp-2 text-center font-medium text-[#974ae4] ${
                                isCard
                                  ? "mt-0.5 text-[10px] leading-tight"
                                  : "text-xs"
                              }`}
                              title={customerTitle}
                            >
                              {customerTitle}
                            </p>
                          </>
                        );
                      })()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vendorStakeholders.map((vendor, rowIndex) => (
                  <tr
                    key={`${vendor.source}-${vendor.id}`}
                    className={rowIndex % 2 ? "bg-gray-50/30" : "bg-white"}
                  >
                    <td
                      className={`sticky left-0 z-20 border-r border-t border-gray-200 align-middle ${
                        isCard ? "px-2.5 py-2.5" : "px-3 py-3"
                      } ${rowIndex % 2 ? "bg-gray-50" : "bg-white"}`}
                    >
                      <p
                        className={`line-clamp-2 font-medium text-gray-900 ${
                          isCard ? "text-xs leading-tight" : "truncate text-sm"
                        }`}
                        title={vendor.name}
                      >
                        {vendor.name}
                      </p>
                      <p
                        className={`line-clamp-1 font-semibold text-[#974ae4] ${
                          isCard
                            ? "mt-0.5 text-[10px] leading-tight"
                            : "truncate text-xs"
                        }`}
                        title={vendor.roleLabel}
                      >
                        {vendor.roleLabel}
                      </p>
                    </td>
                    {customerStakeholders.map(customer => {
                      const key = `${vendor.id}::${customer.user_id}`;
                      const isMapped = activeKeys.has(key);
                      return (
                        <td
                          key={`${vendor.id}-${customer.user_id}`}
                          className={`border-r border-t border-gray-200 text-center ${
                            isCard ? "px-2 py-2.5" : "px-3 py-3"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isMapped}
                            readOnly={!isEditMode}
                            onChange={() => toggleDraftCell(key)}
                            className={`rounded border-gray-300 text-violet-500 focus:ring-violet-500 ${
                              isCard ? "h-3.5 w-3.5" : "h-4 w-4"
                            } ${isEditMode ? "cursor-pointer" : "cursor-default"}`}
                            disabled={!isEditMode}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isCard && customerStakeholders.length > 2 ? (
            <p className="m-0 mt-2 text-center text-[10px] text-gray-400">
              Scroll horizontally and vertically to view all stakeholders
            </p>
          ) : null}
        </div>
      )}
    </>
  );

  if (isCard) {
    return (
      <section
        className={`box-border flex w-full min-w-0 flex-col overflow-hidden rounded-lg border border-[#EBEBEB] bg-white px-4 pb-4 pt-3.5 ${className}`}
      >
        {headerBlock}
        <div className="relative min-h-0 w-full">{bodyContent}</div>
      </section>
    );
  }

  return (
    <section className={PANEL_CLASS}>
      {headerBlock}
      {bodyContent}
    </section>
  );
};

interface CustomerUnlinkConfirmationModalProps {
  target: { memberId: string; memberName: string } | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const CustomerUnlinkConfirmationModal = ({
  target,
  loading,
  onClose,
  onConfirm,
}: CustomerUnlinkConfirmationModalProps) => {
  if (!target) return null;

  return (
    <CommonModal
      isOpen={Boolean(target)}
      toggle={onClose}
      onCloseButton={onClose}
      size="xl"
      showHeader={false}
      nested
      usePortal
    >
      <div className="px-1 py-1">
        <h3 className="mb-3 text-[1.22rem] font-semibold leading-tight text-gray-900 md:text-[1.4rem]">
          Unlink Customer Team Member
        </h3>
        <p className="mb-6 whitespace-pre-line text-[0.94rem] leading-[1.42] text-gray-600 md:text-[1rem]">
          {`Are you sure you want to unlink this customer team member ${target.memberName || ""} from this account?\nThis removes the mapping for this account only.`}
        </p>
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outline-primary"
            onClick={onClose}
            disabled={loading}
            className="min-h-[2.5rem] px-6 text-[1rem]"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={loading}
            className="min-h-[2.5rem] px-6 text-[1rem] font-semibold"
          >
            Yes, Unlink
          </Button>
        </div>
      </div>
    </CommonModal>
  );
};

/** True when the contact still has an active vendor↔contact pair on this account. */
const customerContactHasActiveVendorMapping = (
  relationships: StakeholderRelationship[],
  contactUserId: string
) =>
  relationships.some(item => {
    if (item.active === false) return false;
    if (String(item.customer_team_member || "") !== String(contactUserId)) {
      return false;
    }
    const vendorId =
      item.account_team_member ||
      item.executive_team_member ||
      (item as { vendor_stakeholder?: string }).vendor_stakeholder;
    return Boolean(vendorId);
  });

const TEAM_TAB_CONFIG = [
  { id: "account", label: "Account", Icon: Users },
  { id: "executive", label: "Executive", Icon: Award },
  { id: "customer", label: "Customer", Icon: UsersRound },
] as const;

type TeamsTab = (typeof TEAM_TAB_CONFIG)[number]["id"];

type TeamsCardProps = {
  accountId: string;
  accountName?: string;
  className?: string;
};

const TeamsCard: React.FC<TeamsCardProps> = ({
  accountId,
  accountName = "Account",
  className = "",
}) => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<TeamsTab>("account");
  const [showAllTeamsModal, setShowAllTeamsModal] = useState(false);
  const [isAddAccountTeamOpen, setIsAddAccountTeamOpen] = useState(false);
  const [isAddCustomerTeamOpen, setIsAddCustomerTeamOpen] = useState(false);
  const [isAddExecutiveTeamOpen, setIsAddExecutiveTeamOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EditTeamTarget | null>(null);
  const [updatingTeamMember, setUpdatingTeamMember] = useState(false);
  const [reassignPerson, setReassignPerson] = useState<TeamViewsPerson | null>(
    null
  );
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignPreview, setReassignPreview] = useState<any>(null);
  const [reassignBusyUserId, setReassignBusyUserId] = useState<string | null>(
    null
  );
  const [customerUnlinkTarget, setCustomerUnlinkTarget] = useState<{
    memberId: string;
    memberName: string;
  } | null>(null);
  const [deactivatingCustomerId, setDeactivatingCustomerId] = useState<
    string | null
  >(null);

  const {
    accountTeam,
    customerTeam,
    accountTeamCreate,
    customerTeamCreate,
    executiveTeam,
    executiveTeamCreate,
    accountTeamCandidates,
    stakeholderRelationships,
  } = useAppSelector(state => state.accountDetails);
  const adminUsers = useAppSelector(state => state.master.adminUsers.items);

  useEffect(() => {
    if (!accountId) return;
    dispatch(fetchAccountTeam(accountId));
    dispatch(fetchCustomerTeam(accountId));
    dispatch(fetchExecutiveTeamDetail(accountId));
    dispatch(fetchStakeholderRelationships(accountId));
  }, [dispatch, accountId]);

  useEffect(() => {
    if (!editTarget) return;
    if (adminUsers.length === 0) {
      dispatch(fetchAdminUsers());
    }
  }, [dispatch, editTarget, adminUsers.length]);

  const approverOptions = useMemo(
    () =>
      adminUsers.map(user => {
        const roleName =
          (typeof user.role === "object" ? user.role?.name : user.role) || "";
        const normalizedRole = normalizeRoleName(roleName);
        const roleDisplay =
          (normalizedRole &&
            ROLE_DISPLAY_NAMES[
              normalizedRole as keyof typeof ROLE_DISPLAY_NAMES
            ]) ||
          roleName ||
          "";
        const displayName =
          user.full_name ||
          (user as { name?: string }).name ||
          user.email?.split("@")[0] ||
          "Unknown";
        return {
          label: `${displayName}${roleDisplay ? ` - ${roleDisplay}` : ""}`,
          value: user.user_id,
        };
      }),
    [adminUsers]
  );

  const executiveCandidates = useMemo<ExtraSponsorCandidate[]>(() => {
    const alreadyOnTeam = [
      ...executiveTeam.sponsors.filter(
        member =>
          member.executive_type?.toLowerCase() === "primary" ||
          member.executive_type?.toLowerCase() === "secondary"
      ),
      ...executiveTeam.extraSponsors,
    ];
    const addedIds = new Set(
      alreadyOnTeam
        .flatMap(member => [member.user_id, member.id])
        .filter(Boolean)
    );
    const addedEmails = new Set(
      alreadyOnTeam
        .map(member => member.email?.trim().toLowerCase())
        .filter(Boolean)
    );

    return executiveTeam.organizationUsers
      .filter(user => {
        const normalizedRole = normalizeRoleName(user.role);
        return (
          normalizedRole === ROLES.C_LEVEL ||
          normalizedRole === ROLES.EXECUTIVE_SPONSOR ||
          normalizedRole === ROLES.EXECUTIVE
        );
      })
      .map(user => {
        const email = user.email?.trim().toLowerCase();
        const isAlreadyAdded =
          addedIds.has(user.user_id) || (email && addedEmails.has(email));
        return {
          user_id: user.user_id,
          full_name: user.name,
          email: user.email,
          role_name: user.role || "",
          grade_label: "N/A",
          isAlreadyAdded: Boolean(isAlreadyAdded),
        };
      });
  }, [
    executiveTeam.organizationUsers,
    executiveTeam.extraSponsors,
    executiveTeam.sponsors,
  ]);

  const accountCandidates = useMemo<AccountTeamCandidate[]>(
    () =>
      accountTeamCandidates.items.map(user => ({
        user_id: user.user_id,
        full_name: user.name,
        email: user.email,
        phone: user.phone || "",
        position: user.position || "",
        role_name: user.role || "",
      })),
    [accountTeamCandidates.items]
  );

  const teamsTabs = useMemo(
    () =>
      TEAM_TAB_CONFIG.map(tab => {
        const TabIcon = tab.Icon;
        return {
          id: tab.id,
          label: tab.label,
          icon: (
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-[#974ae4]">
              <TabIcon size={16} strokeWidth={2} />
            </span>
          ),
        };
      }),
    []
  );

  const handleOpenAddAccountTeam = () => {
    setIsAddAccountTeamOpen(true);
    dispatch(clearAccountTeamCreateState());
    if (adminUsers.length === 0) {
      dispatch(fetchAdminUsers());
    }
    dispatch(
      fetchOrganizationUsers({
        roles: ACCOUNT_TEAM_PICKER_ROLES,
        target: "accountTeamCandidates",
      })
    );
  };

  const handleCloseAddAccountTeam = () => {
    setIsAddAccountTeamOpen(false);
    dispatch(clearAccountTeamCreateState());
  };

  const handleOpenAddCustomerTeam = () => {
    setIsAddCustomerTeamOpen(true);
    dispatch(clearAccountTeamCreateState());
    if (adminUsers.length === 0) {
      dispatch(fetchAdminUsers());
    }
  };

  const handleCloseAddCustomerTeam = () => {
    setIsAddCustomerTeamOpen(false);
    dispatch(clearAccountTeamCreateState());
  };

  const handleOpenAddExecutiveTeam = () => {
    setIsAddExecutiveTeamOpen(true);
    dispatch(clearAccountTeamCreateState());
    dispatch(
      fetchOrganizationUsers({
        roles: [...EXECUTIVE_PICKER_ROLES],
        target: "executiveTeam",
      })
    );
  };

  const handleCloseAddExecutiveTeam = () => {
    setIsAddExecutiveTeamOpen(false);
    dispatch(clearAccountTeamCreateState());
  };

  const handleRetryAccountTeam = () => {
    if (accountId) dispatch(fetchAccountTeam(accountId));
  };

  const handleRetryCustomerTeam = () => {
    if (accountId) dispatch(fetchCustomerTeam(accountId));
  };

  const handleRetryExecutiveTeam = () => {
    if (accountId) dispatch(fetchExecutiveTeamDetail(accountId));
  };

  const handleSubmitAccountTeamMember = async (payload: {
    batch_name: string | null;
    approver_id: string;
    members: {
      email: string;
      full_name: string;
      phone: string;
      position: string;
      role: string;
      grade: number;
      languages: string;
    }[];
  }) => {
    if (!accountId) return false;
    try {
      await dispatch(
        createAccountTeamMembersBulk({
          customerId: accountId,
          ...payload,
        })
      ).unwrap();
      dispatch(fetchAccountTeam(accountId));
      dispatch(fetchStakeholderRelationships(accountId));
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmitCustomerTeamMember = async (payload: {
    name: string;
    email: string;
    phone: string;
    role: string;
    position: string;
    language: string;
  }) => {
    if (!accountId) return false;
    try {
      await dispatch(
        createCustomerTeamMember({
          customerId: accountId,
          ...payload,
        })
      ).unwrap();
      dispatch(fetchCustomerTeam(accountId));
      dispatch(fetchStakeholderRelationships(accountId));
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmitExtraSponsors = async (
    members: { user_id: string; reason: string }[]
  ) => {
    if (!accountId) return false;
    try {
      await dispatch(
        createExtraSponsors({
          customerId: accountId,
          members,
        })
      ).unwrap();
      dispatch(fetchExecutiveTeamDetail(accountId));
      dispatch(fetchStakeholderRelationships(accountId));
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmitTeamMemberUpdate = async ({
    memberId,
    teamType,
    body,
  }: {
    memberId: string;
    teamType: "account" | "customer";
    body: Record<string, unknown>;
  }) => {
    if (!accountId) return false;
    try {
      setUpdatingTeamMember(true);
      await dispatch(
        updateTeamMember({
          memberId,
          teamType,
          customerId: accountId,
          payload: body,
        })
      ).unwrap();
      dispatch(fetchAccountTeam(accountId));
      dispatch(fetchCustomerTeam(accountId));
      dispatch(fetchStakeholderRelationships(accountId));
      return true;
    } catch {
      return false;
    } finally {
      setUpdatingTeamMember(false);
    }
  };

  const openReassignForAccountMember = (member: AccountTeamMember) => {
    setReassignPerson(
      buildWorkspaceReassignPerson({
        userId: member.user_id,
        fullName: member.full_name,
        accountId,
        accountName,
        roleName: member.role_name || accountTeamRoleKey(member.role_name),
        roleKey: accountTeamRoleKey(member.role_name),
      })
    );
    setReassignPreview(null);
    setReassignOpen(true);
  };

  const openReassignForExtraSponsor = (member: ExecutiveTeamMember) => {
    const { roleName, roleKey } = extraSponsorReassignRole(member);
    setReassignPerson(
      buildWorkspaceReassignPerson({
        userId: member.user_id,
        fullName: member.full_name,
        accountId,
        accountName,
        roleName,
        roleKey,
      })
    );
    setReassignPreview(null);
    setReassignOpen(true);
  };

  const closeReassign = () => {
    setReassignOpen(false);
    setReassignPerson(null);
    setReassignPreview(null);
    setReassignBusyUserId(null);
  };

  const handleReassignPreview = async (
    selections: { account_id: string; successor_user_id?: string }[]
  ) => {
    if (!reassignPerson) return;
    const data = await dispatch(
      previewTeamReassign({
        person_user_id: reassignPerson.user_id,
        accounts: selections,
      })
    ).unwrap();
    setReassignPreview(data);
  };

  const handleReassignConfirm = async (
    selections: { account_id: string; successor_user_id?: string }[]
  ) => {
    if (!reassignPerson) return;
    try {
      setReassignBusyUserId(reassignPerson.user_id);
      await dispatch(
        applyTeamReassign({
          person_user_id: reassignPerson.user_id,
          accounts: selections,
        })
      ).unwrap();
      toast.success("Reassignment applied");
      closeReassign();
      dispatch(fetchAccountTeam(accountId));
      dispatch(fetchExecutiveTeamDetail(accountId));
      dispatch(fetchStakeholderRelationships(accountId));
    } catch (error: unknown) {
      const message =
        typeof error === "string"
          ? error
          : (error as { message?: string })?.message || "Reassign failed";
      toast.error(message);
    } finally {
      setReassignBusyUserId(null);
    }
  };

  const handleRequestCustomerUnlink = (member: CustomerTeamMember) => {
    if (
      customerContactHasActiveVendorMapping(
        stakeholderRelationships.items,
        member.user_id
      )
    ) {
      toast.warning(
        `${member.name || "This contact"} is paired with vendor team members. Update the relationship on Stakeholder Mapping first, then unlink.`
      );
      return;
    }
    setCustomerUnlinkTarget({
      memberId: member.user_id,
      memberName: member.name || "",
    });
  };

  const handleConfirmCustomerUnlink = async () => {
    if (!accountId || !customerUnlinkTarget) return;
    try {
      setDeactivatingCustomerId(customerUnlinkTarget.memberId);
      await dispatch(
        deactivateTeamMember({
          memberId: customerUnlinkTarget.memberId,
          teamType: "customer",
        })
      ).unwrap();
      toast.success("Team member removed successfully");
      setCustomerUnlinkTarget(null);
      dispatch(fetchCustomerTeam(accountId));
      dispatch(fetchStakeholderRelationships(accountId));
    } catch (error: unknown) {
      const message =
        typeof error === "string"
          ? error
          : (error as { message?: string })?.message ||
            "Failed to remove team member";
      toast.error(message);
    } finally {
      setDeactivatingCustomerId(null);
    }
  };

  const handleActiveTabAdd = () => {
    if (activeTab === "account") {
      handleOpenAddAccountTeam();
    } else if (activeTab === "executive") {
      handleOpenAddExecutiveTeam();
    } else {
      handleOpenAddCustomerTeam();
    }
  };

  return (
    <>
      <section
        className={`box-border flex h-full w-full min-w-0 flex-col overflow-hidden rounded-lg border border-[#EBEBEB] bg-white px-4 pb-3 pt-3.5 ${className}`}
      >
        <div className="flex shrink-0 items-center justify-between gap-3">
          <h2 className="m-0 text-base font-bold leading-6 text-black">
            Teams
          </h2>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setShowAllTeamsModal(true)}
              className="border-0 bg-transparent p-0 text-sm font-semibold leading-6 text-[#7C3AED] hover:underline"
            >
              See All
            </button>
            <Button
              variant="primary"
              className="inline-flex shrink-0 items-center gap-1 px-3 py-1.5 text-xs font-medium"
              onClick={handleActiveTabAdd}
            >
              <Plus size={14} />
              Add
            </Button>
          </div>
        </div>
        <hr className="m-0 mb-3 mt-3 w-full shrink-0 border-0 border-t border-[#F6F6F6]" />

        <div className="shrink-0 border-b border-[#F6F6F6]">
          <TabsComponent
            tabs={teamsTabs}
            activeTab={activeTab}
            onTabChange={(id: string) => setActiveTab(id as TeamsTab)}
            variant="tab-underline"
          />
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden pt-3">
          {activeTab === "account" ? (
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
              <AccountTeamSection
                members={accountTeam.members}
                loading={accountTeam.loading}
                error={accountTeam.error}
                onRetry={handleRetryAccountTeam}
                onAdd={handleOpenAddAccountTeam}
                onEdit={setEditTarget}
                onUnlink={openReassignForAccountMember}
                unlinkingUserId={reassignBusyUserId}
                hideHeader
              />
            </div>
          ) : null}
          {activeTab === "executive" ? (
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
              <ExecutiveTeamSection
                sponsors={executiveTeam.sponsors}
                extraSponsors={executiveTeam.extraSponsors}
                loading={executiveTeam.loading}
                error={executiveTeam.error}
                onRetry={handleRetryExecutiveTeam}
                onAdd={handleOpenAddExecutiveTeam}
                onUnlink={openReassignForExtraSponsor}
                unlinkingUserId={reassignBusyUserId}
                hideHeader
              />
            </div>
          ) : null}
          {activeTab === "customer" ? (
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
              <CustomerTeamSection
                members={customerTeam.members}
                loading={customerTeam.loading}
                error={customerTeam.error}
                onRetry={handleRetryCustomerTeam}
                onAdd={handleOpenAddCustomerTeam}
                onEdit={setEditTarget}
                onUnlink={handleRequestCustomerUnlink}
                unlinkingUserId={deactivatingCustomerId}
                hideHeader
              />
            </div>
          ) : null}
        </div>
      </section>

      <CommonModal
        isOpen={showAllTeamsModal}
        toggle={() => setShowAllTeamsModal(false)}
        size="7xl"
        title={accountName?.trim() || "Account"}
        usePortal
        contentClassName="overflow-y-auto max-h-[78vh]"
        footer={
          <Button variant="primary" onClick={() => setShowAllTeamsModal(false)}>
            Close
          </Button>
        }
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <AccountTeamSection
            members={accountTeam.members}
            loading={accountTeam.loading}
            error={accountTeam.error}
            onRetry={handleRetryAccountTeam}
            onAdd={handleOpenAddAccountTeam}
            onEdit={setEditTarget}
            onUnlink={openReassignForAccountMember}
            unlinkingUserId={reassignBusyUserId}
            expandedScroll
          />
          <ExecutiveTeamSection
            sponsors={executiveTeam.sponsors}
            extraSponsors={executiveTeam.extraSponsors}
            loading={executiveTeam.loading}
            error={executiveTeam.error}
            onRetry={handleRetryExecutiveTeam}
            onAdd={handleOpenAddExecutiveTeam}
            onUnlink={openReassignForExtraSponsor}
            unlinkingUserId={reassignBusyUserId}
            expandedScroll
          />
          <CustomerTeamSection
            members={customerTeam.members}
            loading={customerTeam.loading}
            error={customerTeam.error}
            onRetry={handleRetryCustomerTeam}
            onAdd={handleOpenAddCustomerTeam}
            onEdit={setEditTarget}
            onUnlink={handleRequestCustomerUnlink}
            unlinkingUserId={deactivatingCustomerId}
            expandedScroll
          />
        </div>
      </CommonModal>

      <AddAccountTeamModal
        isOpen={isAddAccountTeamOpen}
        onClose={handleCloseAddAccountTeam}
        accountName={accountName}
        candidates={accountCandidates}
        candidatesLoading={accountTeamCandidates.loading}
        existingMembers={accountTeam.members}
        approverOptions={approverOptions}
        loading={accountTeamCreate.loading}
        submitError={accountTeamCreate.error}
        onSubmit={handleSubmitAccountTeamMember}
      />
      {/* Editing an account team member keeps the original single-member form. */}
      <TeamMemberModal
        isOpen={editTarget?.teamType === "account"}
        onClose={() => setEditTarget(null)}
        accountName={accountName}
        teamType="account"
        mode="edit"
        initialData={editTarget?.teamType === "account" ? editTarget : null}
        approverOptions={approverOptions}
        loading={updatingTeamMember}
        submitError={accountTeamCreate.error}
        onSubmitAccount={handleSubmitAccountTeamMember}
        onSubmitCustomer={handleSubmitCustomerTeamMember}
        onUpdate={handleSubmitTeamMemberUpdate}
      />
      <TeamMemberModal
        isOpen={isAddCustomerTeamOpen || editTarget?.teamType === "customer"}
        onClose={() => {
          if (editTarget?.teamType === "customer") {
            setEditTarget(null);
          } else {
            handleCloseAddCustomerTeam();
          }
        }}
        accountName={accountName}
        teamType="customer"
        mode={editTarget?.teamType === "customer" ? "edit" : "add"}
        initialData={editTarget?.teamType === "customer" ? editTarget : null}
        approverOptions={approverOptions}
        loading={
          editTarget?.teamType === "customer"
            ? updatingTeamMember
            : customerTeamCreate.loading
        }
        submitError={customerTeamCreate.error}
        onSubmitAccount={handleSubmitAccountTeamMember}
        onSubmitCustomer={handleSubmitCustomerTeamMember}
        onUpdate={handleSubmitTeamMemberUpdate}
      />
      <AddExecutiveTeamModal
        isOpen={isAddExecutiveTeamOpen}
        onClose={handleCloseAddExecutiveTeam}
        candidates={executiveCandidates}
        loading={executiveTeamCreate.loading}
        submitError={executiveTeamCreate.error}
        onSubmit={handleSubmitExtraSponsors}
      />
      <ReassignModal
        open={reassignOpen}
        person={reassignPerson}
        focusAccountId={accountId}
        preview={reassignPreview}
        nested={showAllTeamsModal}
        onClose={closeReassign}
        onPreview={handleReassignPreview}
        onConfirm={handleReassignConfirm}
      />
      <CustomerUnlinkConfirmationModal
        target={customerUnlinkTarget}
        loading={Boolean(deactivatingCustomerId)}
        onClose={() => setCustomerUnlinkTarget(null)}
        onConfirm={handleConfirmCustomerUnlink}
      />
    </>
  );
};

export default TeamsCard;
